import { useMemo } from "react";
import { Badge, Card, Text, makeStyles, tokens } from "@fluentui/react-components";
import { slotsToCells, type Weekday } from "@gymplan/shared";
import { AvailabilityGrid } from "./AvailabilityGrid.tsx";

export interface PortalWeek {
  weekStart: string;
  status: "PLANNING" | "HOLIDAY" | "COLLECTING" | "PROPOSED" | "PUBLISHED";
  sessions: { startsAt: string; endsAt: string; status: string; gym: { name: string } | null }[];
}

const useStyles = makeStyles({
  weeks: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px", alignItems: "start", "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
  card: { padding: "12px", display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 },
  head: { display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" },
  note: { color: tokens.colorNeutralForeground3 },
  list: { margin: 0, paddingLeft: "18px" },
});

const local = (iso: string, tz: string) => {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(f.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { weekday: (p.weekday ?? "").slice(0, 3).toUpperCase() as Weekday, time: `${p.hour}:${p.minute}`, day: p.weekday ?? "" };
};
const mondayOf = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
};
const range = (monday: Date) => {
  const sun = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  const f = (x: Date) => x.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${f(monday)} – ${f(sun)}`;
};

function statusLine(w: PortalWeek, which: "this" | "next", trainer: string): string {
  const n = w.sessions.length;
  switch (w.status) {
    case "PUBLISHED":
      return n ? `${n} session${n === 1 ? "" : "s"} booked with ${trainer}.` : `No sessions booked ${which} week.`;
    case "PROPOSED":
      return `${trainer} is finalising ${which} week — your sessions will show here once it's confirmed.`;
    case "COLLECTING":
      return `RackUp is asking about ${which} week now. Reply on WhatsApp, or update your usual times below.`;
    case "HOLIDAY":
      return `${trainer} is away ${which} week, so there are no sessions.`;
    default:
      return which === "this" ? `No sessions booked this week.` : `Not planned yet — RackUp will message you on WhatsApp to check your times.`;
  }
}

/**
 * The client's this week and next, like the trainer's view: their usual times shaded, booked sessions
 * on top, dates on each day and today highlighted. Read-only — times change via the form or WhatsApp.
 */
export function ClientWeeks({ weeks, timezone, trainer, preferences }: { weeks: PortalWeek[]; timezone: string; trainer: string; preferences: { weekday: string; startTime?: string | null; endTime?: string | null }[] }) {
  const s = useStyles();
  const usual = useMemo(
    () => slotsToCells(preferences.map((p) => ({ weekday: p.weekday as Weekday, startTime: p.startTime ?? "06:00", endTime: p.endTime ?? "21:00" }))),
    [preferences],
  );
  return (
    <div className={s.weeks}>
      {weeks.slice(0, 2).map((w, i) => {
        const which = i === 0 ? "this" : "next";
        const monday = mondayOf(w.weekStart);
        const bookings = w.sessions.map((x) => {
          const a = local(x.startsAt, timezone);
          return { weekday: a.weekday, startTime: a.time, endTime: local(x.endsAt, timezone).time, label: "Session" };
        });
        return (
          <Card key={w.weekStart} className={s.card} aria-label={`${which === "this" ? "This" : "Next"} week`}>
            <div className={s.head}>
              <Text weight="semibold" size={500}>{which === "this" ? "This week" : "Next week"}</Text>
              <Text size={200} className={s.note}>{range(monday)}</Text>
              {w.status === "PUBLISHED" && w.sessions.length > 0 && <Badge appearance="tint" color="success">Confirmed</Badge>}
            </div>
            <Text size={200}>{statusLine(w, which, trainer)}</Text>
            {w.sessions.length > 0 && (
              <ul className={s.list}>
                {w.sessions.map((x) => {
                  const a = local(x.startsAt, timezone);
                  return (
                    <li key={x.startsAt}>
                      <Text size={200}>
                        {a.day} {a.time}–{local(x.endsAt, timezone).time}
                        {x.gym ? ` · ${x.gym.name}` : ""}
                      </Text>
                    </li>
                  );
                })}
              </ul>
            )}
            <AvailabilityGrid value={usual} readOnly bare noHint noQuickStart weekStart={monday} bookings={bookings} />
          </Card>
        );
      })}
      <Text size={200} className={s.note} style={{ gridColumn: "1 / -1" }}>
        Shaded: your usual times. Blocks: your booked sessions.
      </Text>
    </div>
  );
}
